import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import React, { forwardRef, Fragment, memo, useCallback, useEffect, useRef, useState, } from 'react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { calculateSelectedIndexes } from './OptionsList.helpers';
import OptionsListOption from './internal/OptionsListOption';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useInternalApp } from '@polestar/component-warehouse-react/contexts/internal';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { useFocusGroup } from '@polestar/component-warehouse-react/hooks/focus';
import { focusProviderOptions } from '@polestar/component-warehouse-react/contexts/focus';
import { firstIndexByText } from '@polestar/component-warehouse-react/components/dropdown-field/DropdownField.helpers';
export var OptionTypes;
(function (OptionTypes) {
    OptionTypes["group"] = "group";
    OptionTypes["option"] = "option";
})(OptionTypes || (OptionTypes = {}));
export var OptionsListSpaceVariants;
(function (OptionsListSpaceVariants) {
    OptionsListSpaceVariants["default"] = "default";
    OptionsListSpaceVariants["fullWidth"] = "fullWidth";
})(OptionsListSpaceVariants || (OptionsListSpaceVariants = {}));
const OptionsList = forwardRef(({ value, options, whitelistedOptionLabels, noOptionsText = '', renderOption, selectedIndex = 0, onKeyUp, onChange, labelId, withBorder = true, multiple = false, internal = false, flatOptions = [], fakeFocusIndex, spacingVariant = OptionsListSpaceVariants.default, focusParentProps, preventFocusScroll = false, ...props }, ref) => {
    if (flatOptions.length === 0) {
        flatOptions = options.slice(0);
    }
    const [searchKey, setSearchKey] = useState('');
    const memoizedId = useSSRSafeId('options-list', props.id);
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    const { createFocusProps, setFocusIndexAndFocus } = useFocusGroup({
        ref: internalRef,
        initialFocusIndex: preventFocusScroll || selectedIndex < 0 ? 0 : selectedIndex,
    });
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchKey) {
                setSearchKey('');
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, [searchKey]);
    const handleKeyUp = useCallback((e) => {
        onKeyUp === null || onKeyUp === void 0 ? void 0 : onKeyUp(e);
        // Down
        switch (e.key) {
            // Blur input
            case ' ':
            case 'Enter':
            case 'Tab':
            case 'Shift':
            case 'Esc':
            case 'Backspace':
                setSearchKey('');
                break;
            default:
                const newSearchKey = `${searchKey}${e.key}`;
                setSearchKey(newSearchKey);
                if (!options.length) {
                    return;
                }
                let firstIndex;
                if (typeof options[0] === 'string') {
                    firstIndex = options.findIndex((item) => item.toLowerCase().startsWith(newSearchKey === null || newSearchKey === void 0 ? void 0 : newSearchKey.toLowerCase()));
                }
                else {
                    firstIndex = firstIndexByText(newSearchKey, flatOptions, whitelistedOptionLabels);
                }
                if (firstIndex !== null) {
                    setFocusIndexAndFocus(firstIndex);
                }
                break;
        }
    }, [onKeyUp, setFocusIndexAndFocus, flatOptions, searchKey, options, whitelistedOptionLabels]);
    const handleChange = useCallback((option) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(option);
    }, [onChange]);
    let optionIndex = -1;
    let previousOption = null;
    const selectedIndexes = calculateSelectedIndexes(options, value);
    const hasGroupedItems = options === null || options === void 0 ? void 0 : options.some((option) => option.type === OptionTypes.group);
    const internalApp = useInternalApp();
    if (internal && !internalApp) {
        // eslint-disable-next-line no-console
        console.warn('OptionsList: you are trying to use a property ("internal") that can only be set with internal projects.');
        internal = false;
    }
    return (React.createElement(Container, { ...aria({
            multiselectable: multiple || undefined,
        }), ...props, ref: combinedRef, withBorder: withBorder, role: "listbox", hasGroupedItems: hasGroupedItems, multiple: multiple, onKeyUp: handleKeyUp }, options && options.length > 0 ? (options.map((option) => {
        var _a, _b, _c, _d;
        if (option.type === OptionTypes.group) {
            const needsSpace = previousOption === OptionTypes.option;
            previousOption = OptionTypes.group;
            // not only the group items are selected also the group header/parent
            // should show its selected status and there the `label` is used
            // while a group has no `value` only `options`
            // @ts-ignore
            const isSelected = selectedIndexes[(_b = (_a = option === null || option === void 0 ? void 0 : option.value) !== null && _a !== void 0 ? _a : option === null || option === void 0 ? void 0 : option.label) !== null && _b !== void 0 ? _b : option];
            const groupLabelIndex = multiple ? ++optionIndex : -1;
            const itemOptions = option.options.map((groupOption) => {
                var _a, _b;
                optionIndex++;
                const selected = selectedIndexes[(_b = (_a = groupOption === null || groupOption === void 0 ? void 0 : groupOption.value) !== null && _a !== void 0 ? _a : groupOption === null || groupOption === void 0 ? void 0 : groupOption.label) !== null && _b !== void 0 ? _b : groupOption];
                return (React.createElement(OptionsListOption, { id: `${memoizedId}-${optionIndex}`, labelId: labelId, key: `options-list-option-${optionIndex}`, selected: selected, value: groupOption, index: optionIndex, isGroupOption: true, isMultiple: multiple, onClick: handleChange, renderOption: renderOption, ...(optionIndex === fakeFocusIndex ? focusParentProps : undefined), spacingVariant: spacingVariant, internal: internal, ...createFocusProps(optionIndex, {
                        forceTabIndex: optionIndex === fakeFocusIndex,
                        fakeFocus: fakeFocusIndex !== undefined,
                    }), ...focusProviderOptions({
                        spacing: -4,
                    }) }));
            });
            // Make sure group header is not focusable when it is not interactive
            const groupLabelFocusProps = multiple
                ? createFocusProps(groupLabelIndex, {
                    forceTabIndex: optionIndex === fakeFocusIndex,
                    fakeFocus: fakeFocusIndex !== undefined,
                })
                : { tabIndex: -1 };
            return (React.createElement(Fragment, { key: `option-group-${optionIndex}` },
                needsSpace && optionIndex > 1 && React.createElement(GroupDivider, { ...aria({ hidden: true }) }),
                React.createElement(OptionGroupContainer, { ...aria({
                        labelledby: `${memoizedId}-${optionIndex}`,
                        role: 'group',
                    }), withBorder: withBorder },
                    React.createElement(OptionsListOption, { id: `${memoizedId}-${groupLabelIndex}-${optionIndex}`, labelId: labelId, key: `options-list-option-${groupLabelIndex}`, value: option, selected: isSelected, index: groupLabelIndex, isGroupOption: true, isGroupLabel: true, isMultiple: multiple, onClick: handleChange, renderOption: renderOption, spacingVariant: spacingVariant, internal: internal, ...(optionIndex === fakeFocusIndex ? focusParentProps : undefined), ...groupLabelFocusProps, ...focusProviderOptions({
                            spacing: -4,
                        }) }),
                    itemOptions)));
        }
        else {
            optionIndex++;
            const selected = selectedIndexes[(_d = (_c = option === null || option === void 0 ? void 0 : option.value) !== null && _c !== void 0 ? _c : option === null || option === void 0 ? void 0 : option.label) !== null && _d !== void 0 ? _d : option];
            previousOption = OptionTypes.option;
            const focusProps = createFocusProps(optionIndex, {
                forceTabIndex: optionIndex === fakeFocusIndex,
                fakeFocus: fakeFocusIndex !== undefined,
            });
            const itemOption = (React.createElement(OptionsListOption, { id: `${memoizedId}-${optionIndex}`, labelId: labelId, key: `options-list-option-${optionIndex}-${optionIndex === fakeFocusIndex}`, selected: selected, value: option, index: optionIndex, isGroupOption: false, isMultiple: multiple, onClick: handleChange, renderOption: renderOption, ...(optionIndex === fakeFocusIndex ? focusParentProps : undefined), spacingVariant: spacingVariant, internal: internal, ...focusProps, ...focusProviderOptions({
                    spacing: -4,
                }) }));
            return (React.createElement(Fragment, { key: `option-${optionIndex}-${optionIndex === fakeFocusIndex}` }, itemOption));
        }
    })) : (React.createElement(OptionsListOption, { id: memoizedId, index: 0, disabled: true, selected: false, hasNoOptions: true, spacingVariant: spacingVariant, internal: internal }, noOptionsText))));
});
OptionsList.displayName = 'OptionsList';
export default memo(OptionsList);
const Container = styled.div(({ withBorder, theme, hasGroupedItems, multiple }) => css `
    height: 100%;
    max-height: ${withBorder ? '40vh' : '100%'};
    background-color: ${theme.optionsList.backgroundColor};
    border: ${withBorder ? `1px solid ${theme.optionsList.borderColor}` : 'none'};
    overflow: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    width: 100%;

    ${responsiveStyles({
    /* hasGroupedItems check is added to requested by design due to 48px it felt off with the 4px padding on top and bottom */
    padding: {
        mobile: '0 0',
        tablet: hasGroupedItems && multiple ? undefined : `${Spacings.xxSmall} 0`,
    },
})}
  `);
const OptionGroupContainer = styled.div(({ withBorder }) => css([
    css `
      margin: 0;
      padding: 0;
    `,
    !withBorder &&
        css `
        &:first-of-type {
          padding-block-start: 0;
        }
      `,
]));
const GroupDivider = styled.div(({ theme }) => css `
    display: flex;
    position: relative;
    width: 100%;

    &:after {
      content: '';
      width: 100%;
      height: 1px;
      background: ${theme.optionsList.borderColor};
      ${responsiveStyles({
    margin: { mobile: `0 ${Spacings.large}`, tablet: 0 },
})}
    }
  `);
